import Highcharts from 'highcharts';
import 'highcharts/modules/map';
import 'highcharts/modules/tiledwebmap';
import 'highcharts/modules/boost';
import '../../highcharts-options';
import Offcanvas from 'bootstrap/js/dist/offcanvas'

import { discreteColorscale } from '../../colorscale';
import * as utils from '../../../utils';
import { update as evalUpdate, callbackGlobStat as evalCallbackGlobStat } from '../../../modules/evaluation';
import { update as interUpdate, callbackGlobStat as interCallbackGlobStat } from '../../../modules/intercomp';

import './index.css';
import * as mapConfigData from './config.json';

import { Aeroval } from '../../../types/global';
import { MapData, TrendsStats } from '../../../types/data';
import { MapConfig } from '../../../types/config';
import { toSvgText } from '../../../utils';
const mapConfig = mapConfigData as unknown as MapConfig;

declare var aeroval: Aeroval;

export async function plotMap(data: MapData, parameterDir: string, statisticDir: string, id: string, _chartOptions: any = {}): Promise<void> {
	const mapElement = document.getElementById(id);
	if (!mapElement) return;

	let scale: number[] = []
	const customMin = aeroval.chartOptions?.map.min ? aeroval.chartOptions.map.min : _chartOptions.min
	const customMax = aeroval.chartOptions?.map.max ? aeroval.chartOptions.map.max : _chartOptions.max
	const customStep = aeroval.chartOptions?.map.step ? aeroval.chartOptions.map.step : _chartOptions.step

	if (customMin !== undefined && customMax !== undefined && customStep !== undefined) {
		scale = utils.linearArray(customMin, customMax, customStep)
	} else {
		scale = getScale(statisticDir, parameterDir)
	}
	const legend = getLegend(scale, statisticDir, parameterDir);
	const stations: any[] = [];
	const stationsName: string[] = [];


	//trends update: if the statistics has a /, then split this and show two statistics (obs and model trends)
	if (statisticDir.includes('/')) {
		const firstUnderscore = statisticDir.indexOf('_');
		const stat1 = `${statisticDir.split('/')[0]}_${statisticDir.slice(firstUnderscore + 1)}`;
		const stat2 = statisticDir.split('/')[1];
		var stats = [stat2, stat1];
	} else {
		stats = [statisticDir];
	}

	for (const stat of stats) {
		if (stat.includes('mod')) {
			var symbol = 'square';
			var radiusFactor = 1.2;
			var zIndex = 0;
			var lineWidth = 0;
			var lineColor = aeroval.settings.theme == 'dark' ? '#E0E0E0' : '#404040';
		} else {
			symbol = getMarkerSymbol() as string;
			radiusFactor = 1;
			zIndex = 1;
			lineWidth = getMarkerOutline();
			if (stats.length > 1) {
				lineWidth = 1;
			}
			lineColor = aeroval.settings.theme == 'dark' ? '#E0E0E0' : '#404040';
		}

		for (const stationData of data) {
			const keyStat = aeroval.frequency;
			const keyTime = utils.getKeyTime(aeroval.time, aeroval.season);
			if (!(keyStat in stationData)) break;
			const statisticsData = stationData[keyStat]?.[keyTime];
			if (!statisticsData) continue;

			// get value from statistics data: nrms is not defined in the files and need to be computed
			var value: number | undefined = undefined;
			if (stat === 'nrms' && statisticsData['rms'] && statisticsData['refdata_mean']) {
				value = (statisticsData['rms'] as number) / Math.abs(statisticsData['refdata_mean'] as number);
			} else if (statisticsData[stat] != null) {
				// other statistics need to be defined
				if (stat.includes('trend') && (statisticsData[stat] as TrendsStats)['map_var']) {
					var mapVar = (statisticsData[stat] as TrendsStats)['map_var'];
					value = (statisticsData[stat] as TrendsStats)[mapVar] as number;
				} else {
					value = statisticsData[stat] as number;
				}
			}

			// the trends are already provided in percent per year
			if (aeroval.statistics[statisticDir].unit === '%' && value) {
				value = value * 100;
			}

			// set up color
			const color = value != null ? utils.colStat(value, scale, statisticDir) : '#adadad';

			// set up radius
			var radius = _chartOptions.markerRadius ? _chartOptions.markerRadius : 1.5;
			const stationsRadius = document.getElementById('stationsRadius');
			if (stationsRadius instanceof HTMLInputElement) {
				radius = parseFloat(stationsRadius.value) - 0.75;
			}


			// limit digits
			const statistic = value != null ? value.toFixed(3) : null;

			// MODIS fix
			const name = typeof stationData['station_name'] !== 'string' ? `(${(stationData['station_name'][0] as number).toFixed(1)}, ${(stationData['station_name'][1] as number).toFixed(1)})` : stationData['station_name'];
			const stationName = stationData.station_display_name ? stationData.station_display_name : name
			const lat = parseFloat(stationData['latitude']?.toFixed(4));
			const lon = parseFloat(stationData['longitude']?.toFixed(4));
			const alt = parseFloat(stationData['altitude']?.toFixed(1));

			const station = {
				name: stationName,
				id: name,
				lat,
				lon,
				alt,
				marker: {
					radius: radius * radiusFactor,
					fillColor: color,
					symbol,
					zIndex,
					lineColor,
					lineWidth,
				},
				// more entries for the table
				region: stationData['region'],
				statistic,
				[keyStat]: stationData[keyStat],
				zIndex,
			};
			if (statistic != null) {
				stations.push(station);
			}
			stationsName.push(name);
		}
	}

	// set map zoom and position, title and subtitle
	const mapZoomKey = aeroval.cfg.webdisp_opts.map_zoom;
	var [center, zoom] = await getMapInitialPosition();
	center = isValidMapCenter(center) ? center : getDefaultCenter(mapZoomKey);
	zoom = isValidMapZoom(zoom) ? zoom : getDefaultZoom(mapZoomKey);
	if (Number.isFinite(_chartOptions.deltaZoom)) {
		zoom += _chartOptions.deltaZoom;
	}
	zoom = clampMapZoom(zoom, mapZoomKey);
	const titleText = getTitle(parameterDir);
	const subTitleText = getSubTitle();

	// load world map
	const activeTopologyKey = (aeroval as any).activeTopologyKey;
	const shouldReloadTopology = !aeroval.topology || activeTopologyKey !== mapZoomKey;

	if (shouldReloadTopology) {
		let mapUrl = ''
		switch (mapZoomKey) {
			case 'World':
				mapUrl = '../../geography/highcharts-maps/world-highres.topo.json';
				break;
			case 'Europe':
				mapUrl = '../../geography/highcharts-maps/world-highres2.topo.json';
				break;
			case 'Norway':
				mapUrl = '../../geography/highcharts-maps/no-jm-all.topo.json';
				break;
			default:
				mapUrl = '../../geography/highcharts-maps/world-highres.topo.json';
				break;
		}

		aeroval.topology = await fetch(mapUrl).then((response) => response.json());
		(aeroval as any).activeTopologyKey = mapZoomKey;

		// Reset persisted view on topology changes to avoid invalid center/zoom carryover.
		if (activeTopologyKey && activeTopologyKey !== mapZoomKey) {
			aeroval.mapCenter = undefined;
			aeroval.mapZoom = undefined;
		}
	}

	const opacityRangeElement = document.querySelector('#opacityRange')
	const nullOpacity = opacityRangeElement instanceof HTMLInputElement ? Number(opacityRangeElement.value) : 0.5

	const mapBackground = getMapBackground()
	const projectionName = getProjectionName(mapZoomKey, mapBackground);
	const baseMap = mapBackground == 'OSM'
		? {
			type: "tiledwebmap",
			name: "Basemap Tiles",
			animation: false,
			provider: {
				type: "OpenStreetMap"
			},
			showInLegend: false
		}
		: {
			name: 'Basemap',
			zIndex: 0,
			showInLegend: false,
			animation: false,
			borderWidth: 1,
			borderColor: '#ffffff',
			nullColor: '#bebebe',
			opacity: nullOpacity
		}

	// Initiate the chart
	const chartOptions: any = {
		chart: {
			renderTo: id,
			animation: false,
			events: {
				load: function () {
					const mapView = (this as any).mapView;
					if (!mapView || !isValidMapCenter(mapView.center) || !isValidMapZoom(mapView.zoom)) return;

					// Force one post-load projection pass so inset/island geometry
					// aligns correctly on first paint (e.g. Jan Mayen/Svalbard).
					mapView.setView(mapView.center, mapView.zoom, false, false);
				}
			}
		},
		exporting: {
			chartOptions: {
				chart: {
					marginBottom: 50,
					marginLeft: 0,
					marginRight: 0
				},
				mapView: {
					center: aeroval.mapCenter ?? center,
					zoom: aeroval.mapZoom ?? zoom
				}
			},
		},
		title: {
			text: toSvgText(titleText),
		},
		subtitle: {
			text: toSvgText(subTitleText),
		},
		mapNavigation: {
			enabled: true,
			enableButtons: false
		},
		mapView: {
			center,
			zoom,
			maxZoom: getMaxZoom(mapZoomKey),
			...(projectionName ? {
				projection: {
					name: projectionName
				}
			} : {}),
			padding: getMapPadding(mapZoomKey)
		},
		tooltip: {
			headerFormat: '',
			pointFormat: `<b>{point.name}</b><br>Lat: {point.lat}, Lon: {point.lon}<br>Alt: {point.alt} m <br>${aeroval.statistics[statisticDir].name}: {point.statistic}`,
			valueDecimals: 2,
		},
		plotOptions: {
			series: {
				turboThreshold: 0,
			},
		},
		legend: {
			useHTML: true,
			floating: true,
			align: 'center',
			padding: 0,
			symbolRadius: 0,
			symbolWidth: 0,
			symbolPadding: 0,
			labelFormatter: function () {
				return legend as string;
			},
			events: {
				itemClick: function (event: any) {
					event?.preventDefault()
				}
			}
		},
		series: [
			baseMap,
			{
				// Specify points using lat/lon
				type: 'mappoint',
				name: 'Stations',
				animation: false,
				data: stations,
				marker: {
					symbol: 'circle',
				},
				stickyTracking: false,
				cursor: 'pointer',
				dataLabels: {
					enabled: false,
				},
				showInLegend: true,
				point: {
					events: {
						legendItemClick: function () {
							return false; // <== returning false will cancel the default action
						},
						mouseOver: function () {
							if (aeroval.settings.presentationMode === 'true') return;
							// check if not in 3d tab
							toggleMaplLoader(true);
							aeroval.wait = false;
							const that = this as any;
							aeroval.timeOver = setTimeout(function () {
								if (aeroval.wait === false) {
									aeroval.station = that.options.id;
									if (window.location.pathname.includes('/evaluation')) {
										evalUpdate('station', that.options.id);
									} else if (window.location.pathname.includes('/intercomp')) {
										interUpdate('station', that.options.id);
									} else {
										utils.todo();
									}
									toggleMaplLoader(false);
								}
							}, 2000);
						},
						mouseOut: function () {
							if (aeroval.isMobile) return;
							aeroval.wait = true;
							clearTimeout(aeroval.timeOver);
							toggleMaplLoader(false);
						},
						click: function () {
							aeroval.wait = false;
							clearTimeout(aeroval.timeOver);
							const that = this as any;
							aeroval.station = that.options.id;
							if (window.location.pathname.includes('/evaluation')) {
								evalUpdate('station', that.options.id);
							} else if (window.location.pathname.includes('/intercomp')) {
								interUpdate('station', that.options.id);
							} else {
								utils.todo();
							}
							toggleMaplLoader(false);
						},
					},
				},
			},
		] as any,
	};

	// Only add topology map for non-OSM backgrounds
	if (mapBackground !== 'OSM') {
		chartOptions.chart.map = aeroval.topology;
	}

	// Initiate the chart
	Highcharts.mapChart(
		chartOptions,
		function (chart: any) {
			let isClampingView = false;
			if (chart.mapView) {
				Highcharts.addEvent(chart.mapView, 'afterSetView', function (this: any) {
					if (isClampingView) return;
					if (!isValidMapCenter(this.center) || !isValidMapZoom(this.zoom)) return;

					const boundedZoom = clampMapZoom(this.zoom, mapZoomKey);
					if (Math.abs(boundedZoom - this.zoom) > 1e-6) {
						isClampingView = true;
						this.setView(this.center, boundedZoom, false, false);
						chart.redraw(false);
						isClampingView = false;
					}
				});
			}

			Highcharts.addEvent(chart, 'render', function (this: any) {
				if (this.mapView && this.options.exporting?.chartOptions) {
					const viewCenter = isValidMapCenter(this.mapView.center) ? this.mapView.center : center;
					const viewZoom = isValidMapZoom(this.mapView.zoom)
						? clampMapZoom(this.mapView.zoom, mapZoomKey)
						: zoom;
					this.options.exporting.chartOptions.mapView = {
						center: viewCenter,
						zoom: viewZoom,
					};
				}
			});

			const mapElement = document.querySelector('#map');
			if (mapElement) {
				mapElement.addEventListener('mouseout', () => {
					if (chart && chart.mapView) {
						const viewCenter = chart.mapView.center;
						const viewZoom = chart.mapView.zoom;
						if (isValidMapCenter(viewCenter) && isValidMapZoom(viewZoom)) {
							aeroval.mapCenter = viewCenter;
							aeroval.mapZoom = clampMapZoom(viewZoom, mapZoomKey);
						}
					}
				});
			}

			//send the list of stations to the map-form
			const mapFormElement = document.getElementById('map-form')
			const searchList = [...stationsName, ...Object.keys(aeroval.regions)];
			if (mapFormElement instanceof HTMLInputElement) {
				autocomplete(mapFormElement, searchList);
			}

			// add regions
			plotRegions(chart);
			alignNorwayInitialInsets(chart, mapZoomKey, projectionName);

			// print map Table
			const mapTableElement = document.getElementById('map-table');
			if (mapTableElement instanceof HTMLElement) {
				mapTable(data);
			}

			// add offcanvas event
			offcanvasLegend(chart, scale)
		}
	);

	// if click on a country, hide the station
	// Get all elements with class 'highcharts-background'
	const highchartsBackgrounds = document.querySelectorAll('.highcharts-background');
	highchartsBackgrounds.forEach((background) => {
		background.addEventListener('click', () => {
			hideSelectedStation();
			hideAllRegions();
		});
	});

	// if click on export button, hide station by default
	// Get the parent element
	const parentElement = document.querySelector('#map > * > svg > g.highcharts-exporting-group > g');
	if (parentElement) {
		// Add event listener for 'click' event
		parentElement.addEventListener('click', () => {
			hideSelectedStation();
			hideAllRegions();
		});
	}

	if (aeroval.data.map && aeroval.checkMap != false) {
		checkStation(aeroval.data.map);
	}

	if (aeroval.station) showStation(aeroval.station);
	if (aeroval.region && aeroval.region !== 'ALL' && aeroval.region !== 'WORLD') showRegion(aeroval.region, aeroval.settings.zoomOnSelectedRegion === 'true');

}

function toggleMaplLoader(show: boolean) {
	const loader = document.querySelector('#hover-loader-map');
	if (!(loader instanceof HTMLElement)) return;

	loader.classList.toggle('show', show);
	loader.classList.toggle('hide', !show);
}

function alignNorwayInitialInsets(chart: Highcharts.Chart, mapZoomKey: string, projectionName?: string): void {
	// Needed only for Norway when using topology-native projection.
	if (mapZoomKey !== 'Norway' || projectionName) return;
	const mapView = (chart as any).mapView;
	if (!mapView || !isValidMapCenter(mapView.center) || !isValidMapZoom(mapView.zoom)) return;

	requestAnimationFrame(() => {
		chart.reflow();
		const center = mapView.center;
		const zoom = mapView.zoom;
		if (!isValidMapCenter(center) || !isValidMapZoom(zoom)) return;
		// Passing redraw=true here is essential: MapView.setView() only
		// recomputes each inset's hitZone/playingField (their on-screen
		// box), but the actual SVG transform used to place points/paths
		// inside that box is only re-applied when the map-geometry
		// series redraws. A plain chart.redraw() does NOT mark those
		// series dirty (Chart.redraw only re-renders a series if
		// isDirtyBox or series.isDirty is set), so it silently no-ops.
		// MapView's own redraw() wrapper does mark useMapGeometry
		// series dirty before calling chart.redraw(), which is what
		// setView(..., true, ...) triggers internally.
		mapView.setView(center, zoom, true, false);
	});
}

async function getMapInitialPosition(): Promise<[[number, number] | undefined, number | undefined]> {
	var center: undefined | [number, number] = undefined;
	var zoom: undefined | number = undefined;
	if (mapConfig && aeroval.cfg.webdisp_opts.map_zoom in mapConfig) {
		center = mapConfig[aeroval.cfg.webdisp_opts.map_zoom].center;
		zoom = mapConfig[aeroval.cfg.webdisp_opts.map_zoom].zoom;
	}
	if (isValidMapCenter(aeroval.mapCenter) && isValidMapZoom(aeroval.mapZoom)) {
		center = aeroval.mapCenter;
		zoom = aeroval.mapZoom;
	}
	return [center, zoom];
}

function isValidMapCenter(center: unknown): center is [number, number] {
	if (!Array.isArray(center) || center.length !== 2) return false;
	const [lon, lat] = center;
	if (typeof lon !== 'number' || typeof lat !== 'number') return false;
	if (!Number.isFinite(lon) || !Number.isFinite(lat)) return false;
	return lon >= -180 && lon <= 180 && lat >= -90 && lat <= 90;
}

function isValidMapZoom(zoom: unknown): zoom is number {
	if (typeof zoom !== 'number') return false;
	if (!Number.isFinite(zoom)) return false;
	return zoom >= -5 && zoom <= 20;
}

function getDefaultCenter(mapZoomKey: string): [number, number] {
	if (mapConfig && mapZoomKey in mapConfig && Array.isArray(mapConfig[mapZoomKey].center)) {
		return mapConfig[mapZoomKey].center as [number, number];
	}
	return [0, 0];
}

function getDefaultZoom(mapZoomKey: string): number {
	if (mapConfig && mapZoomKey in mapConfig && typeof mapConfig[mapZoomKey].zoom === 'number') {
		return mapConfig[mapZoomKey].zoom;
	}
	return 1;
}

function clampMapZoom(zoom: number, mapZoomKey: string): number {
	const minZoom = getMinZoom(mapZoomKey);
	const maxZoom = getMaxZoom(mapZoomKey);
	const epsilon = 1e-3;
	if (zoom < minZoom - epsilon) return minZoom;
	if (zoom > maxZoom + epsilon) return maxZoom;
	return zoom;
}

function getMinZoom(mapZoomKey: string): number {
	if (mapConfig && mapZoomKey in mapConfig && typeof mapConfig[mapZoomKey].min_zoom === 'number') {
		return mapConfig[mapZoomKey].min_zoom;
	}
	return mapZoomKey === 'Norway' ? 2 : -1;
}

function getMaxZoom(mapZoomKey: string): number {
	if (mapConfig && mapZoomKey in mapConfig && typeof mapConfig[mapZoomKey].max_zoom === 'number') {
		return mapConfig[mapZoomKey].max_zoom;
	}
	return mapZoomKey === 'Norway' ? 10 : 20;
}

function getMapPadding(mapZoomKey: string): number {
	if (mapConfig && mapZoomKey in mapConfig && typeof mapConfig[mapZoomKey].padding === 'number') {
		return mapConfig[mapZoomKey].padding;
	}
	return 0;
}

function getProjectionName(mapZoomKey: string, mapBackground?: string): string | undefined {
	if (mapBackground === 'OSM') {
		return 'WebMercator';
	}
	if (mapConfig && mapZoomKey in mapConfig && typeof mapConfig[mapZoomKey].projection === 'string') {
		return mapConfig[mapZoomKey].projection;
	}
	return undefined;
}

function getLegend(scale: number[], statisticDir: string, parameterDir: string): string | undefined {
	var legend: string | undefined = undefined;

	const legSize = 12;
	const unit = utils.getUnits(statisticDir);
	const legendUnit = unit === '(1)' ? '' : ` ${unit}`;

	legend = `
		<div class="legend-container w-100 p-0" style="display: flex; flex-direction: column; align-items: center;">
		<b>${aeroval.statistics[statisticDir].name}${legendUnit}</b>
		<table id='tabLegend' class='map-legend frosted-glass'>
		<tr>`

	for (let i: number = 0; i < scale.length; i++) {
		let name = scale[i].toString();
		if (i == 0) name = `${name}<`;
		if (i == scale.length - 1) name = `>${name}`
		legend += `<td style='width:${legSize}px'>${name}</td>`;
		if (i < scale.length - 1) {
			legend += `<td>
				<span class='legend-item' style='color:${discreteColorscale(scale[i], scale, parameterDir, statisticDir, 'linear')}'>●</span>
				</td>`
		}
	}
	legend += `</tr></table></div>`;

	return legend;
}

function getScale(statisticDir: string, parameterDir: string): number[] {
	if (!aeroval.statistics[statisticDir].scale) {
		var scale = aeroval.ranges[parameterDir].scale;
	} else {
		if (aeroval.statistics[statisticDir].unit === 'var') {
			const statScale = aeroval.ranges[parameterDir].scale;
			scale = aeroval.statistics[statisticDir].scale.map((x) => x * Math.max(...statScale));
			scale = scale.map((x) => parseFloat(x.toFixed(2))) as number[];
		} else {
			scale = aeroval.statistics[statisticDir].scale;
		}
	}
	return scale;
}

function getMarkerSymbol(): string | undefined {
	// get marker symbol from man menu
	let markerSymbol = 'circle';
	const markerInputs = document.querySelectorAll('#markerSymbol>input');
	for (let input of markerInputs) {
		var element = input as HTMLInputElement;
		if (element.checked) {
			markerSymbol = element.value;
		}
	}
	return markerSymbol;
}

function getMarkerOutline(): number {
	// get marker symbol from man menu
	let markerOutline = 'false';
	const markerInputs = document.querySelectorAll('#markerOutline>input');
	for (let input of markerInputs) {
		var element = input as HTMLInputElement;
		if (element.checked) {
			markerOutline = element.value;
		}
	}
	const markerLineWidth = markerOutline == 'false' ? 0 : 1
	return markerLineWidth;
}

function getMapBackground(): string | undefined {
	// get marker symbol from man menu
	var value = undefined
	const mapBackgroundInputs = document.querySelectorAll('#mapBackground>input');
	for (let input of mapBackgroundInputs) {
		var element = input as HTMLInputElement;
		if (element.checked) {
			value = element.value;
		}
	}
	return value;
}

function mapTable(stations: MapData): void {
	const mapTableElement = document.getElementById('map-table');
	if (!mapTableElement) return;

	/*mapTable*/
	const keyStat = aeroval.frequency;
	const keyTime = utils.getKeyTime(aeroval.time, aeroval.season);
	const statistics = Object(aeroval.statistics).keys;

	var h = `<table class='table table-hover table-condensed slideTable'>
		<thead>
		<tr>
		<th rowspan=2>Station</th>
		<th rowspan=2>Lat</th>
		<th rowspan=2>Lon</th>
		<th rowspan=2>Region</th>
		<th colspan=${statistics}>Statistics</th>
		</tr>
		<tr>
		<th>${aeroval.statistic.name}</th>
		</tr>
		</thead>
		<tbody>`
	for (let station of stations) {
		let statisticsData = station[keyStat]?.[keyTime];
		if (!statisticsData) continue;
		h += `<tr>
			<td>${station.station_name}</td>
			<td>${station.latitude.toFixed(2)}</td>
			<td>${station.longitude.toFixed(2)}</td>
			<td>${station.region}</td>`
		if (!station[keyStat]) continue;
		if (aeroval.statistic.dir == 'obs/mod_trend') {
			const statObs = statisticsData['obs_trend'] as TrendsStats;
			const statMod = statisticsData['mod_trend'] as TrendsStats;
			const mapVarObs = statObs.map_var;
			const mapVarMod = statMod.map_var;
			const slopeY0Obs = statObs[mapVarObs];
			const slopeY0Mod = statMod[mapVarMod];
			h += `<td> ${slopeY0Obs?.toFixed(3)} / ${slopeY0Mod?.toFixed(3)}</td > `;
		} else {
			var statistic = null
			if (aeroval.statistic.dir === 'nrms' && statisticsData['rms'] && statisticsData['refdata_mean']) {
				statistic = (statisticsData['rms'] as number) / (statisticsData['refdata_mean'] as number);
			} else {
				statistic = statisticsData[aeroval.statistic.dir];
			}

			if (!statistic) continue;
			//if the statistic is a trend, this is an object and not a number
			if (typeof statistic === 'number') {
				h += statistic != null ? `<td>${statistic.toFixed(3)}</td>` : `<td>${statistic}</td>`;
			} else if (typeof statistic == 'object') {
				//print slp as respect to first year
				const mapVar = (statistic as TrendsStats).map_var;
				const slopeY0 = (statistic as TrendsStats)[mapVar];
				h += `<td>${slopeY0?.toFixed(3)}</td>`;
			}
		}
		h += '</tr>';
	}
	h += `</tbody>
		</table>`;

	mapTableElement.innerHTML = h;
}

function plotRegions(chart: Highcharts.Chart) {
	const regColor = "var(--highcharts-neutral-color-60)";

	//check if region is defined as a rectangle
	for (let region in aeroval.regions) {
		//if rectangle
		if (aeroval.regions[region].minLon) {
			//get coordinates - specific case of World, since the map does not go to -90 in lat
			const minLon = aeroval.regions[region].minLon;
			const maxLon = aeroval.regions[region].maxLon;
			const minLat = aeroval.regions[region].minLat;
			const maxLat = aeroval.regions[region].maxLat;

			// Avoid rendering full-globe rectangle overlays (e.g. ALL), which can
			// create projection artifacts around the dateline/poles in mapline series.
			const coversWholeWorld = minLon <= -180 && maxLon >= 180 && minLat <= -90 && maxLat >= 90;
			if (coversWholeWorld) {
				continue;
			}

			// points defining regions boundaries
			if (minLon <= maxLon) {
				const longitudes = [minLon, maxLon, maxLon, minLon, minLon];
				const latitudes = [minLat, minLat, maxLat, maxLat, minLat];
				const coordinates = [];
				for (const j in longitudes) {
					coordinates.push([longitudes[j], latitudes[j]]);
				}

				// plot lines in between each point
				chart.addSeries(
					{
						id: region,
						type: 'mapline',
						data: [
							{
								geometry: {
									type: 'LineString',
									coordinates: coordinates,
								},
							},
						],
						lineWidth: 1,
						affectsMapView: false,
						zIndex: 10,
						color: regColor,
						nullColor: regColor,
						showInLegend: false,
						visible: false,
					},
					false,
					false
				);

				// else, the region shall be split in two parts
			} else {
				// draw left part
				const leftLongitudes = [-180, maxLon, maxLon, -180];
				const leftLatitudes = [minLat, minLat, maxLat, maxLat];
				const leftCoordinates = [];
				for (const j in leftLongitudes) {
					leftCoordinates.push([leftLongitudes[j], leftLatitudes[j]]);
				}

				// plot lines in between each point
				chart.addSeries(
					{
						id: region,
						type: 'mapline',
						data: [
							{
								geometry: {
									type: 'LineString',
									coordinates: leftCoordinates,
								},
							},
						],
						lineWidth: 1,
						affectsMapView: false,
						zIndex: 10,
						color: regColor,
						nullColor: regColor,
						showInLegend: false,
						visible: false,
					} as any,
					false,
					false
				);

				// draw right part
				const rightLongitudes = [180, minLon, minLon, 180];
				const rightLatitudes = [minLat, minLat, maxLat, maxLat];
				const rightCoordinates = [];
				for (let j in rightLongitudes) {
					rightCoordinates.push([rightLongitudes[j], rightLatitudes[j]]);
				}

				// plot lines in between each point
				chart.addSeries(
					{
						id: `${region}-2`,
						//linkedTo: region,
						type: 'mapline',
						data: [
							{
								geometry: {
									type: 'LineString',
									coordinates: rightCoordinates,
								},
							},
						],
						lineWidth: 1,
						affectsMapView: false,
						zIndex: 10,
						color: regColor,
						nullColor: regColor,
						showInLegend: false,
						visible: false,
					},
					false,
					false
				);
			}

			// if lons lats arrays
		} else if (aeroval.regions[region].lats) {
			//get coordinates - specific case of World, since the map does not go to -90 in lat
			const longitudes = aeroval.regions[region].lons as any[];
			const latitudes = aeroval.regions[region].lats as any[];
			const coordinates = [];
			for (const j in longitudes) {
				coordinates.push([longitudes[j], latitudes[j]]);
			}

			// plot lines in between each point
			chart.addSeries(
				{
					id: region,
					type: 'mapline',
					data: [
						{
							geometry: {
								type: 'LineString',
								coordinates: coordinates,
							},
						},
					],
					lineWidth: 1,
					affectsMapView: false,
					zIndex: 10,
					color: regColor,
					nullColor: regColor,
					showInLegend: false,
					visible: false,
				},
				false,
				false
			);
		} else {
			const regionCodes = Array.isArray(aeroval.regions[region])
				? aeroval.regions[region]
				: [aeroval.regions[region]];
			const data = regionCodes
				.filter((code) => typeof code === 'string' && code.length > 0)
				.map((code) => ({
					code: code.toLowerCase(),
					value: 1,
				}));
			if (region != '') {
				chart.addSeries(
					{
						name: region,
						id: region,
						borderWidth: 1,
						affectsMapView: false,
						borderColor: 'rgba(255, 165, 0, 1)',
						states: {
							hover: {
								borderColor: 'rgba(255, 165, 0, 1)',
							}
						},
						zIndex: 100,
						color: 'rgba(255, 165, 0, 0.20)',
						keys: ['code', 'value'],
						joinBy: ['hc-key', 'code'],
						allAreas: false,
						data: data,
						dataLabels: {
							enabled: true,
							color: '#FFFFFF',
							formatter: function () {
								return (this as any).point.name;
							},
						},
						showInLegend: false,
						visible: false,
					} as any,
					false
				);
			}
		}
	}
	// redraw chart
	chart.redraw();
}

export function checkStation(data: MapData) {
	if ((!aeroval.station && !aeroval.region) || ((aeroval.region) && !(aeroval.region in aeroval.regions))) {
		if (window.location.pathname.includes('/evaluation')) {
			evalCallbackGlobStat(true, true)
		} else if (window.location.pathname.includes('/intercomp')) {
			interCallbackGlobStat(true, true)
		} else {
			utils.todo();
		}
	} else if (aeroval.station) {
		// extracts coordinates to find closest station if station not in list
		const stations: string[] = [];
		const lats: number[] = [];
		const lons: number[] = [];
		for (let item of data) {
			stations.push(item.station_name);
			lats.push(item.latitude);
			lons.push(item.longitude);
		}
		const regionKeys = Object.keys(aeroval.regions);
		const hasKnownSelection = stations.includes(aeroval.station) || regionKeys.includes(aeroval.station);

		if (!hasKnownSelection) {
			const iClosest = utils.calcDist(aeroval.lastLatitude, aeroval.lastLongitude, lats, lons);
			aeroval.station = stations[iClosest];
			if (window.location.pathname.includes('/evaluation')) {
				evalUpdate('station', aeroval.station);
			} else if (window.location.pathname.includes('/intercomp')) {
				interUpdate('station', aeroval.station);
			} else {
				utils.todo();
			}
		}
	}

}

export function showStation(station_name: string) {
	const chart = utils.getChartById('map');
	if (!chart || !aeroval.data.map) return;

	// hide all regions
	hideAllRegions();

	// unhighlight selected region in regional chart
	utils.highlightSelectedRegionInRegionalChart('reg-stat', '');

	//remove previously selected stations
	if (typeof chart.get('SelectedStation') != 'undefined') {
		(chart as any).get('SelectedStation').remove();
	}

	// set up color
	const color = aeroval.settings.theme == 'dark' ? '#ffffff' : '#000000';

	// grep info of selected station
	var selectedStation = undefined;
	for (let stationData of aeroval.data.map) {
		if (stationData.station_name == station_name) {
			const lat = stationData.latitude;
			const lon = stationData.longitude;
			const stationName = stationData.station_display_name ? stationData.station_display_name : stationData.station_name;
			selectedStation = {
				name: stationName,
				lat,
				lon,
				color: '#404040',
			};
			aeroval.lastLatitude = selectedStation.lat;
			aeroval.lastLongitude = selectedStation.lon;
			break;
		}
	}

	if (selectedStation) {
		const selectedSeries = {
			type: 'mappoint',
			name: 'SelectedStation',
			id: 'SelectedStation',
			data: [selectedStation],
			zIndex: 1000,
			marker: {
				symbol: 'circle',
				radius: Number((document.querySelector('#stationsRadius') as HTMLInputElement).value) / 2,
				color,
			},
			stickyTracking: false,
			dataLabels: {
				enabled: true,
				color: '#404040',
			},
			showInLegend: false,
			enableMouseTracking: false,
		} as any;

		//send the serie to the map
		chart.addSeries(selectedSeries, true, false);

		//if mouse not in map
		if (aeroval.goto) {
			aeroval.mapCenter = [selectedStation.lon, selectedStation.lat];
			aeroval.mapZoom = 5;
			(chart as any).mapView.setView(aeroval.mapCenter, aeroval.mapZoom);
			aeroval.goto = false;
		}
	}
}

export function hideSelectedStation() {
	const chart = utils.getChartById('map');
	//remove previously selected stations
	if (chart) {
		(chart as any).get('SelectedStation')?.remove();
	}
}

function getWrappedLongitudeMetrics(minLon: number, maxLon: number): { span: number, center: number, crossesDateline: boolean } {
	const crossesDateline = minLon > maxLon;
	const span = crossesDateline ? (maxLon + 360) - minLon : maxLon - minLon;
	let center = minLon + span / 2;
	if (center > 180) center -= 360;

	return { span, center, crossesDateline };
}

export function showRegion(region: string, zoomOnRegion: boolean) {
	const chart = utils.getChartById('map');
	if (!chart) return;
	const mapZoomKey = aeroval.cfg.webdisp_opts.map_zoom;
	let appliedProgrammaticZoom = false;

	// hide all regions
	for (let reg in aeroval.regions) {
		if (chart.get(reg)) {
			(chart.get(reg) as Highcharts.Series).setVisible(false, false);
		}
	}

	// show selected region
	if (chart.get(region)) {
		(chart.get(region) as Highcharts.Series).setVisible(true, false);
	}

	// zoom on region
	if (zoomOnRegion) {
		if (aeroval.regions[region].minLon) {
			const minLon = aeroval.regions[region].minLon;
			const maxLon = aeroval.regions[region].maxLon;
			const minLat = aeroval.regions[region].minLat;
			const maxLat = aeroval.regions[region].maxLat;
			const { span: wrappedLonSpan, center: centerLon, crossesDateline } = getWrappedLongitudeMetrics(minLon, maxLon);
			const mapView = (chart as any).mapView;
			let appliedFitToBounds = false;

			if (mapView?.fitToBounds && !crossesDateline) {
				const p1 = mapView.lonLatToProjectedUnits({ lon: minLon, lat: minLat });
				const p2 = mapView.lonLatToProjectedUnits({ lon: maxLon, lat: maxLat });

				if (p1 && p2) {
					mapView.fitToBounds({
						x1: Math.min(p1.x, p2.x),
						y1: Math.min(p1.y, p2.y),
						x2: Math.max(p1.x, p2.x),
						y2: Math.max(p1.y, p2.y),
					});
					aeroval.mapCenter = mapView.center;
					aeroval.mapZoom = clampMapZoom(mapView.zoom, mapZoomKey);
					appliedFitToBounds = true;
					appliedProgrammaticZoom = true;
				}
			}

			// Fallback for dateline-spanning boxes or missing projection helpers.
			if (!appliedFitToBounds) {
				const centerLat = (minLat + maxLat) / 2;
				const deltaLon = wrappedLonSpan;
				const deltaLat = Math.abs(maxLat - minLat);
				const deltaMax = Math.max(deltaLon, deltaLat);
				let zoom = 0;
				if (deltaMax > 0) {
					zoom = Math.floor(Math.log2(360 / deltaMax));
					if (crossesDateline) zoom += 2;
					zoom = Math.min(zoom, 10); // limit zoom to 10
				}

				aeroval.mapCenter = [centerLon, centerLat];
				aeroval.mapZoom = clampMapZoom(zoom, mapZoomKey);
				mapView?.setView(aeroval.mapCenter, aeroval.mapZoom);
				appliedProgrammaticZoom = true;
			}

		}
		// if region is a country code, zoom to this country
		else if (aeroval.regions[region].length > 0) {
			const regionSeries = chart.get(region) as Highcharts.Series | undefined;
			if (regionSeries && !regionSeries.visible) {
				regionSeries.setVisible(true, false);
			}

			// Ensure region geometry is up-to-date before trying to zoom to it.
			chart.redraw();

			const mapView = (chart as any).mapView;
			const regionPoint = (regionSeries as any)?.points?.find((point: any) => point?.bounds) ?? (regionSeries as any)?.points?.[0];

			if (regionPoint?.bounds && mapView?.fitToBounds) {
				mapView.fitToBounds(regionPoint.bounds);
				aeroval.mapCenter = mapView.center;
				aeroval.mapZoom = clampMapZoom(mapView.zoom, mapZoomKey);
				appliedProgrammaticZoom = true;
			} else if (regionPoint?.zoomTo) {
				regionPoint.zoomTo();
				aeroval.mapCenter = mapView?.center;
				aeroval.mapZoom = mapView?.zoom != null ? clampMapZoom(mapView.zoom, mapZoomKey) : undefined;
				appliedProgrammaticZoom = true;
			}
		}
	}

	if (appliedProgrammaticZoom) {
		const mapView = (chart as any).mapView;
		if (mapView && isValidMapCenter(mapView.center) && isValidMapZoom(mapView.zoom)) {
			mapView.setView(mapView.center, clampMapZoom(mapView.zoom, mapZoomKey), false, false);
		}
	}

	chart.redraw();
}

export function hideRegion(region: string) {
	const chart = utils.getChartById('map');
	if (!chart) return;

	// hide selected region
	if (chart.get(region)) {
		(chart.get(region) as Highcharts.Series).setVisible(false, false);
	}

	chart.redraw();
}

export function hideAllRegions() {
	const chart = utils.getChartById('map');
	if (!chart) return;

	// hide all regions
	for (let reg in aeroval.regions) {
		if (chart.get(reg)) {
			(chart.get(reg) as Highcharts.Series).setVisible(false, false);
		}
	}

	chart.redraw();
}

function autocomplete(inp: HTMLInputElement, arr: string[]) {
	let currentFocus: number;

	inp.addEventListener('input', () => {
		let a: HTMLDivElement,
			b: HTMLDivElement,
			val: string = inp.value;
		closeAllLists();
		if (!val) {
			return false;
		}
		currentFocus = -1;
		a = document.createElement('DIV') as HTMLDivElement;
		a.setAttribute('id', inp.id + 'autocomplete-list');
		a.setAttribute('class', 'autocomplete-items');
		inp.parentNode?.appendChild(a);
		for (let ar of arr) {
			if (ar.substring(0, val.length).toUpperCase() == val.toUpperCase()) {
				b = document.createElement('DIV') as HTMLDivElement;
				b.innerHTML = `<strong>${ar.substring(0, val.length)}</strong>${ar.substring(val.length)}<input type='hidden' value='${ar}'>`
				b.addEventListener('click', function () {
					inp.value = this.getElementsByTagName('input')[0].value;
					aeroval.goto = true;
					if (window.location.pathname.includes('/evaluation')) {
						if (inp.value in aeroval.regions) {
							evalUpdate('region', inp.value);
						} else {
							evalUpdate('station', inp.value);
						}
					} else if (window.location.pathname.includes('/intercomp')) {
						if (inp.value in aeroval.regions) {
							interUpdate('region', inp.value);
						} else {
							interUpdate('station', inp.value)
						}
					} else {
						utils.todo();
					}
					closeAllLists();
				});
				a.appendChild(b);
			}
		}
	});

	inp.addEventListener('keydown', (e) => {
		let x: HTMLDivElement[] | undefined = undefined;
		let tempX = document.getElementById(inp.id + 'autocomplete-list');
		if (tempX) {
			x = Array.from(tempX.getElementsByTagName('div')) as HTMLDivElement[];
		}
		if (e.key == 'ArrowDown') {
			currentFocus++;
			addActive(x);
		} else if (e.key == 'ArrowUp') {
			currentFocus--;
			addActive(x);
		} else if (e.key == 'Enter') {
			e.preventDefault();
			if (currentFocus > -1) {
				if (x) x[currentFocus].click();
			}
		}
	});

	function addActive(x: HTMLDivElement[] | undefined) {
		if (!x) return false;
		removeActive(x);
		if (currentFocus >= x.length) currentFocus = 0;
		if (currentFocus < 0) currentFocus = x.length - 1;
		x[currentFocus].classList.add('autocomplete-active');
	}

	function removeActive(x: HTMLDivElement[]) {
		for (let item of Array.from(x)) {
			item.classList.remove('autocomplete-active');
		}
	}

	function closeAllLists(element?: HTMLElement) {
		let items = document.getElementsByClassName('autocomplete-items');
		for (let item of Array.from(items)) {
			if (element != item && element != inp) {
				item.parentNode?.removeChild(item);
			}
		}
	}

	document.addEventListener('click', function (e) {
		closeAllLists(e.target as HTMLElement);
	});
}

function getTitle(parameterDir: string): string {
	var title = aeroval.menu[parameterDir].name;
	if (aeroval.layer != 'Surface' && aeroval.layer != 'Column') {
		title += ` (${aeroval.layer})`;
	}
	title += ` - ${aeroval.time}`;
	if (aeroval.season != 'All') {
		title += ` (${aeroval.season})`;
	}
	return title;
}

function getSubTitle(): string {
	if (aeroval.interModelStatistic) {
		var subtitle = `intercomparison (${aeroval.interModelStatistic.name}) - ${aeroval.frequency} data`;
	} else {
		if (aeroval.statistic.name.includes('Mod')) {
			var subtitle = `${aeroval.model.name} - ${aeroval.frequency} data`;
		} else if (aeroval.statistic.name.includes('Obs')) {
			var subtitle = `${aeroval.observation} - ${aeroval.frequency} data`;
		} else {
			var subtitle = `${aeroval.observation} - ${aeroval.model.name} - ${aeroval.frequency} data`;
		}
	}

	return subtitle;
}

function offcanvasLegend(chart: Highcharts.Chart, scale: number[]) {

	const offcanvasId = `${chart.container.id}AxisOffcanvas`;
	const minScale = scale[0] ?? -1
	const maxScale = scale[scale.length - 1] ?? -1
	const stepScale = scale[1] - scale[0]

	var stepInput = utils.getLowerPowerOfTen(stepScale)

	// insures the step is small
	if (stepInput) stepInput /= 10

	// Create offcanvas content
	const offcanvasContent = `
			<div class="offcanvas offcanvas-end" tabindex="-1" id="${offcanvasId}" aria-labelledby="${offcanvasId}Label">
				<div class="offcanvas-header">
					<h5 class="offcanvas-title" id="${offcanvasId}Label">Map legend configuration</h5>
					<button type="button" class="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
				</div>
				<div class="offcanvas-body">
					<form id="${offcanvasId}Form">
						<div class="mb-3">
							<label for="${offcanvasId}minScale" class="form-label">Min</label>
							<input type="number" class="form-control" id="${offcanvasId}minScaleInput" value="${minScale}" step="${stepInput}"/>
						</div>
						<div class="mb-3">
							<label for="${offcanvasId}maxScale" class="form-label">Max</label>
							<input type="number" class="form-control" id="${offcanvasId}maxScaleInput" value="${maxScale}" step="${stepInput}"/>
						</div>
						<div class="mb-3">
							<label for="${offcanvasId}StepAxis" class="form-label">Step</label>
							<input type="number" class="form-control" id="${offcanvasId}stepScaleInput" value="${stepScale}" step="${stepInput}"/>
						</div>
						<button type="submit" class="btn btn-primary">Update chart</button>
					</form>
				</div>
			</div>
		`;

	// Create offcanvas element
	const offcanvasElement = document.createElement('div');
	offcanvasElement.innerHTML = offcanvasContent;
	document.body.appendChild(offcanvasElement);

	// Define event listener inside loop
	const activeElement = document.querySelector(`#${chart.container.id} .highcharts-legend g .map-legend`)
	if (activeElement) activeElement.addEventListener("click", (function (offcanvasId) {
		return function () {
			const canvasElement = document.getElementById(offcanvasId);
			if (!canvasElement) return;
			const offcanvas = new Offcanvas(canvasElement);
			offcanvas.show();
		};
	})(offcanvasId));

	// Add submit event listener to the form
	const form = document.getElementById(`${offcanvasId}Form`);
	if (!form) return;

	form.addEventListener('submit', function (event) {
		event.preventDefault(); // Prevent default form submission behavior

		// Get input elements
		const minInput = document.querySelector(`#${offcanvasId}minScaleInput`) as HTMLInputElement;
		const maxInput = document.querySelector(`#${offcanvasId}maxScaleInput`) as HTMLInputElement;
		const stepInput = document.querySelector(`#${offcanvasId}stepScaleInput`) as HTMLInputElement;

		// Get min and max values
		const min = parseFloat(minInput.value);
		const max = parseFloat(maxInput.value);
		const step = parseFloat(stepInput.value);

		// Update axis only if min or max values are valid
		if (!isNaN(min) || !isNaN(max)) {
			const mapData = aeroval.data.map
			if (!aeroval.chartOptions) aeroval.chartOptions = {}
			aeroval.chartOptions.map = { min: min, max: max, step: step }
			if (mapData) plotMap(mapData, aeroval.parameter.dir, aeroval.statistic.dir, 'map', aeroval.chartOptions.map)
		}
	});
}